import * as React from "react";
import { FindEquipmentAdditionalFilter } from "../../../components/FindEquipmentAdditionalFilter";
import { AppTranslations } from "../../../../../../components/Utils/translations/AppTranslations";
import {
	AutoSuggestionItemProps,
	InputAddon,
	Spinner,
	SpinnerSize,
	useDebounce,
} from "@vebox/etui-core";
import {
	INPUT_BORDER_BOX,
	Styles,
} from "../../../../../../components/common/etui_components_styles";
import { FindEquipmentContext } from "../../../providers/FindEquipmentProvider";
import { useLocationsSuggestions } from "../../../data/hooks/CecnsusCoolerControllerHooks";
import VeBoxAutoSuggest from "../../../components/VeBoxAutoSuggest";
import { createSiteFilter, mapLocationAndGroupedByTypes } from "./utils";
import { HierarchyTreeForFilters } from "../../HierarchyTreeForFilters";
import { ADDITIONAL_FILTERS } from "../../../types";
import { Div } from "@vebox/etui-system-elements";

declare var AppTranslations: AppTranslations;

const INPUT_BORDER_BOX_WITH_MARGIN: Styles = {
	...INPUT_BORDER_BOX,
	root: {
		mb: 10,
	},
	input: {
		...INPUT_BORDER_BOX.input,
		borderRadius: 3,
	},
};

interface SitesFilterFilterProps {
	open?: boolean;
}

export const SitesFilter: React.FC<SitesFilterFilterProps> = ({ open }) => {
	const { selectedAdditionalFilter, handleVeBoxAdditionalFilters } =
		React.useContext(FindEquipmentContext);
	const [partialLabel, setPartialLabel] = React.useState<string>();
	const [labelValue, setLabelValue] = useDebounce(partialLabel, 500);
	const [selectedSite, setSelectedSite] =
		React.useState<AutoSuggestionItemProps>(undefined);
	const [fetchSuggestions, setFetchSuggestions] = React.useState<boolean>(true);
	const [canOpen, setCanOpen] = React.useState<boolean>(false);

	const locationsSuggestions = useLocationsSuggestions(
		labelValue?.length && fetchSuggestions,
		labelValue,
	);
	const suggestions = React.useMemo(() => {
		return locationsSuggestions?.isLoading
			? []
			: mapLocationAndGroupedByTypes(locationsSuggestions?.data ?? []);
	}, [locationsSuggestions?.data, locationsSuggestions?.isLoading]);

	const isLoadingLocations = React.useMemo(
		() => locationsSuggestions?.isLoading || locationsSuggestions?.isValidating,
		[locationsSuggestions?.isLoading, locationsSuggestions?.isValidating],
	);

	const isActive = React.useMemo(
		() =>
			selectedAdditionalFilter.includes(ADDITIONAL_FILTERS.HIERARCHY) ||
			selectedAdditionalFilter.includes(
				ADDITIONAL_FILTERS.COMMERCIAL_HIERARCHY,
			) ||
			selectedAdditionalFilter.includes(
				ADDITIONAL_FILTERS.LOCATION_GENERIC_LABEL,
			),
		[selectedAdditionalFilter],
	);

	const clearSiteValue = () => {
		setPartialLabel("");
	};
	const renderItemToString = (item: AutoSuggestionItemProps) => {
		return item?.label ?? "";
	};
	const handleSiteChange = (value: string) => {
		setCanOpen(false);
		setPartialLabel(value);
		if (selectedSite && selectedSite.label === value && fetchSuggestions) {
			setFetchSuggestions(false);
		}
		if (selectedSite && selectedSite.label !== value && !fetchSuggestions) {
			setFetchSuggestions(true);
		}
	};
	const onSelectedValueChange = (suggestion: AutoSuggestionItemProps) => {
		setFetchSuggestions(false);
		setSelectedSite(suggestion);
	};

	const renderAutoSuggestInputAddon = () => {
		if (isLoadingLocations) {
			return (
				<InputAddon position="end">
					<Spinner size={SpinnerSize.xSmall} />
				</InputAddon>
			);
		}
		if (!isLoadingLocations && Boolean(partialLabel)) {
			return (
				<InputAddon position="end">
					<span
						className="material-icons-outlined clickable"
						onClick={clearSiteValue}
					>
						clear
					</span>
				</InputAddon>
			);
		}
		return null;
	};

	React.useEffect(() => {
		setLabelValue(partialLabel);
	}, [partialLabel]);

	React.useEffect(() => {
		if (isLoadingLocations) {
			setCanOpen(true);
		}
	}, [isLoadingLocations]);

	React.useEffect(() => {
		if (selectedSite) {
			handleVeBoxAdditionalFilters(
				"add",
				createSiteFilter(selectedSite),
				ADDITIONAL_FILTERS.LOCATION_GENERIC_LABEL,
			);
		}
	}, [selectedSite]);

	return (
		<FindEquipmentAdditionalFilter
			name={AppTranslations.filters.equipment.sitesTitle}
			active={isActive}
			open={open}
			openIfActive={true}
		>
			<Div style={{ display: "flex", flexDirection: "column" }}>
				<VeBoxAutoSuggest
					label={AppTranslations.filters.equipment.location}
					styles={INPUT_BORDER_BOX_WITH_MARGIN as any}
					emptyPlaceholder={AppTranslations.noDataAvailable}
					groupedSuggestions={suggestions}
					suggestions={[]}
					itemToString={renderItemToString}
					onInputValueChange={handleSiteChange}
					inputValue={partialLabel}
					onSelectedSuggestion={onSelectedValueChange}
					selectedItem={selectedSite}
					excludeSelected={true}
					endInputAddon={renderAutoSuggestInputAddon()}
					openOnFocus={false}
					closeOnLoading={true}
					isLoading={isLoadingLocations}
					canOpen={canOpen || suggestions?.length > 0}
				/>
				<HierarchyTreeForFilters />
			</Div>
		</FindEquipmentAdditionalFilter>
	);
};
